import type { AuthLogin, AuthPassword } from "~/types/schemas/autenticacao";
import { AutenticacaoApi } from "~/api/autenticacao/autenticacao";
import type { Usuario } from "~/types/schemas/usuario";
import { Warn } from "@sieg-ad/error";
import { USER_ROLES } from "~/constants/roles";
import { useEscolaStore } from "./escola";
import { defineStore } from "pinia";


export type AuthState = {
  authenticated: boolean;
  user: Usuario | null;
  token: string | null;
  lastUpdate: Date;
  loading: boolean;
  message: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    authenticated: false,
    user: null,
    token: null,
    lastUpdate: new Date(),
    loading: false,
    message: null,
  }),
  getters: {
    roleId: (state) => state.user?.tipo_id ?? null,
    isAdministrador: (state) => state.user?.tipo_id === USER_ROLES.ADMINISTRADOR,
    isGestor: (state) => state.user?.tipo_id === USER_ROLES.GESTOR,
    isDocente: (state) => state.user?.tipo_id === USER_ROLES.DOCENTE,
    isAluno: (state) => state.user?.tipo_id === USER_ROLES.ALUNO,
    isSuporte: (state) => state.user?.tipo_id === USER_ROLES.SUPORTE,
  },
  persist: {
    //? `sameSite` barra o envio do cookie por outro site; `secure` só no HTTPS (o teste local é http).
    storage: piniaPluginPersistedstate.cookies({
      maxAge: 60 * 60 * 24 * 30,
      priority: "high",
      sameSite: "strict",
      secure: typeof location !== "undefined" && location.protocol === "https:",
    }),
    pick: ['authenticated', 'user', 'token'],
  },
  actions: {
    async authenticate(options: AuthLogin) {
      if (this.loading) return;

      this.loading = true;
      
      try {
        if (!options.login) throw new Warn('Preencha o campo de login');
        if (!options.senha) throw new Warn('Preencha o campo de senha');

        const { data } = await AutenticacaoApi.login(options);

        //@ts-ignore
        this.token = data.token;
        //@ts-ignore
        this.user = data.usuario;
        this.authenticated = true;
        this.lastUpdate = new Date();
      }
      catch (error) {
        this.message = error.message;
        throw error;
      }
      finally {
        this.loading = false;
      }
    },

    setUser(user: Usuario | null) {
      const current = JSON.stringify(this.user);  
      const newUser = JSON.stringify(user);

      if (current === newUser) return;
      this.user = user;
    },

    async changePassword(options: AuthPassword) {
      try {
        //? A troca derruba as sessões antigas na API, inclusive esta: segue com o token novo.
        const { data } = await AutenticacaoApi.senha(options);
        if (data?.token) this.token = data.token;
        return { success: true }
      }
      catch (error) {
        return { success: false }
      }
    },

    async forgetPassword(options: Partial<AuthLogin>) {
      await AutenticacaoApi.forgetPassword(options);
    },

    async logout() {
      this.resetStore();
      useRouter().push('/login');
    },

    resetStore() {
      localStorage.clear();
      sessionStorage.clear();
      this.$reset();
      //? A escola escolhida vive na store em memória: sem isto o próximo login na mesma aba
      //? seguia mandando o x-escola-id do usuário anterior.
      useEscolaStore().resetEscola();
    }
  }
})
